import { useState, useEffect } from "react";
import { openDB } from "idb";
import { VictoryChart, VictoryLine, VictoryAxis, VictoryTheme, VictoryScatter } from "victory";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import "@styles/GeneralPages.css";
import "@styles/Scales.css";
import "@assets/constants/colours.css";

const DB_NAME = "workoutDB";
const DB_VERSION = 25;

const grey = getComputedStyle(document.documentElement).getPropertyValue('--op_grey');
const blue = getComputedStyle(document.documentElement).getPropertyValue('--bright_primary');
const dark_blue = getComputedStyle(document.documentElement).getPropertyValue('--primary');
 
const Scales = () => {
  const [weightLogs, setWeightLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [weight, setWeight] = useState("");
  const [currentDateRange, setCurrentDateRange] = useState(new Date()); // Track the current date range focus
  const [viewRange, setViewRange] = useState("week");

  const formatDateForInput = (date) => {
    const d = new Date(date);
    return d.toISOString().slice(0, 16); // Extracts 'yyyy-MM-ddTHH:mm'
  };

  const [logDate, setLogDate] = useState(formatDateForInput(new Date()));
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const fetchWeightLogs = async () => {
      try {
        const db = await openDB(DB_NAME, DB_VERSION);
        const logs = await db.getAll("user_weight_log");
        setWeightLogs(logs);
      } catch (error) {
        console.error("Error fetching weight logs:", error);
      }
    };

    fetchWeightLogs();
  }, []);

  const navigateDateRange = (direction) => {
    const newDate = new Date(currentDateRange);
    
    switch (viewRange) {
      case "week":
        direction === 'prev' 
          ? newDate.setDate(newDate.getDate() - 7)
          : newDate.setDate(newDate.getDate() + 7);
        break;
      case "month":
        direction === 'prev' 
          ? newDate.setMonth(newDate.getMonth() - 1)
          : newDate.setMonth(newDate.getMonth() + 1);
        break;
      case "6months":
        direction === 'prev' 
          ? newDate.setMonth(newDate.getMonth() - 6)
          : newDate.setMonth(newDate.getMonth() + 6);
        break;
      case "year":
        direction === 'prev' 
          ? newDate.setFullYear(newDate.getFullYear() - 1)
          : newDate.setFullYear(newDate.getFullYear() + 1);
        break;
      default:
        break;
    }
    
    setCurrentDateRange(newDate);
  };

  const getRangeTitle = () => {
    const startDate = new Date(currentDateRange);
    const endDate = new Date(currentDateRange);
    
    switch (viewRange) {
      case "week":
        startDate.setDate(startDate.getDate() - 6); // Show 7 days total
        return `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
      case "month":
        return startDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      case "6months":
        startDate.setMonth(startDate.getMonth() - 5); // Show 6 months total
        return `${startDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
      case "year":
        return startDate.toLocaleDateString('en-GB', { year: 'numeric' });
      default:
        return "All Time";
    }
  };

  const filterLogs = () => {
    const endDate = new Date(currentDateRange);
    let startDate = new Date(currentDateRange);
  
    switch (viewRange) {
      case "week":
        startDate.setDate(endDate.getDate() - 6);
        break;
      case "month":
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case "6months":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "year":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default:
        startDate = null;
    }
  
    let filteredLogs = weightLogs;
  
    if (startDate) {
      filteredLogs = weightLogs.filter(log => {
        const logDate = new Date(log.log_date);
        return logDate >= startDate && logDate <= endDate;
      });
    }
  
    // Group logs by date and compute average weight
    const groupedLogs = {};
    filteredLogs.forEach(log => {
      const logDate = new Date(log.log_date);
      // Normalize to start of day
      logDate.setHours(0, 0, 0, 0);
      const dateKey = logDate.toISOString();
      
      if (!groupedLogs[dateKey]) {
        groupedLogs[dateKey] = { sum: 0, count: 0 };
      }
      groupedLogs[dateKey].sum += log.weight;
      groupedLogs[dateKey].count += 1;
    });
  
    const groupedEntries = Object.entries(groupedLogs).map(([date, { sum, count }]) => ({
      x: new Date(date),
      y: sum / count,
    }));
  
    groupedEntries.sort((a, b) => a.x - b.x);
  
    return groupedEntries;
  };

  const addOrUpdateWeightLog = async () => {
    try {
      const db = await openDB(DB_NAME, DB_VERSION);
      const logData = { weight: parseFloat(weight), log_date: new Date(logDate).toISOString() };

      if (editId !== null) {
        // Edit existing entry
        await db.put("user_weight_log", { id: editId, ...logData });
      } else {
        // Add new entry
        await db.add("user_weight_log", logData);
      }

      const logs = await db.getAll("user_weight_log");
      setWeightLogs(logs);

      // Reset modal state
      setIsModalOpen(false);
      setWeight("");
      setLogDate(formatDateForInput(new Date())); // Reset to current date
      setEditId(null);
    } catch (error) {
      console.error("Error adding or updating weight log:", error);
    }
  };

  const deleteWeightLog = async (id) => {
    if (!id) {
      console.error("Error: No ID provided for deletion.");
      return;
    }

    console.log("Deleting log with id:", id);

    try {
      const db = await openDB(DB_NAME, DB_VERSION);
      await db.delete("user_weight_log", id);
      const logs = await db.getAll("user_weight_log");
      setWeightLogs(logs);
    } catch (error) {
      console.error("Error deleting weight log:", error);
    }
  };

  const openEditModal = (log) => {
    setWeight(log.weight);
    setLogDate(new Date(log.log_date).toISOString().slice(0, 16)); // Format for datetime-local input
    setEditId(log.id);
    setIsModalOpen(true);
  };

  const calculateAxisDomain = (data) => {
    if (data.length === 0) return [0, 100];
    
    const values = data.map((d) => d.y);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    const roundedMin = Math.floor(min / 5) * 5 -1;
    
    const roundedMax = Math.ceil(max / 5) * 5 + 1;
    
    const minRange = 10;
    const calculatedRange = roundedMax - roundedMin;
    
    if (calculatedRange < minRange) {
      const padding = Math.ceil((minRange - calculatedRange) / 10) * 5;
      return [roundedMin - padding, roundedMax + padding];
    }
    
    return [roundedMin, roundedMax];
  };

  const getTickValues = (data) => {
    if (data.length === 0) return [0, 25, 50, 75, 100]; // Default ticks
    
    const [min, max] = calculateAxisDomain(data);
    const ticks = [];
    
    // Always include 0 if it's within or near the range
    if (min <= 0 || (min > 0 && min < 10)) {
      ticks.push(0);
    }
    
    // Generate ticks every 5 units within the range
    for (let i = Math.ceil(min / 5) * 5; i <= max; i += 5) {
      ticks.push(i);
    }
    
    return ticks;
  };

  const getXAxisTickValues = () => {
    const now = new Date();
    let startDate = new Date();
    let interval = 'day';
  
    switch (viewRange) {
      case "week":
        startDate.setDate(now.getDate() - 7);
        interval = 'day';
        break;
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        interval = 'week';
        break;
      case "6months":
        startDate.setMonth(now.getMonth() - 6);
        interval = 'month';
        break;
      case "year":
        startDate.setFullYear(now.getFullYear() - 1);
        interval = 'month';
        break;
      default: // "all"
        if (weightLogs.length > 0) {
          startDate = new Date(Math.min(...weightLogs.map(log => new Date(log.log_date))));
        }
        interval = 'month';
    }
  
    const dates = [];
    let currentDate = new Date(startDate);
    
    while (currentDate <= now) {
      dates.push(new Date(currentDate));
      
      if (interval === 'day') {
        currentDate.setDate(currentDate.getDate() + 1);
      } else if (interval === 'week') {
        currentDate.setDate(currentDate.getDate() + 7);
      } else { // month
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }
  
    return dates;
  };

  const decidePlotSize = () => {
    switch (viewRange) {
      case "week":
        return 3;
        break;
      case "month":
        return 2;
        break;
      case "6months":
        return 2;
        break;
      case "year":
        return 1.5;
        break;
      default:
        return 3;
    }
  };

  return (
    <div className="page-content calendar-page">
      {/* View Range Toggle and Navigation */}
      <div className="calendar-header">
        <div className="toggle-container">
          {["week", "month", "6months", "year"].map((range) => (
            <button
              key={range}
              className={viewRange === range ? "active" : ""}
              onClick={() => {
                setViewRange(range);
                setCurrentDateRange(new Date()); // Reset to current date when changing range
              }}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>

      </div>

      <div className="navigation-container wide">
        <button 
          onClick={() => navigateDateRange('prev')}
          aria-label={`Previous ${viewRange}`}
        >
          <IoIosArrowBack />
        </button>
        <h2 className="h2-background">{getRangeTitle()}</h2>
        <button 
          onClick={() => navigateDateRange('next')}
          aria-label={`Next ${viewRange}`}
        >
          <IoIosArrowForward />
        </button> 
      </div>

      {/* Weight Graph */}
      <div className="chart-container">
        <VictoryChart 
          theme={VictoryTheme.clean} 
          scale={{ x: "time" }} 
          padding={{ top: 10, bottom: 50, left: 50, right: 50 }} 
          style={{
            background: {
              fill: grey,
              marginTop: 10,
            }
          }}
        >
          <VictoryAxis
            tickValues={(() => {
              const endDate = new Date(currentDateRange);
              let startDate = new Date(currentDateRange);
              const dates = [];
              let currentDate;

              switch (viewRange) {
                case "week":
                  startDate.setDate(endDate.getDate() - 7);
                  currentDate = new Date(startDate);
                  currentDate.setHours(0, 0, 0, 0);
                  while (currentDate <= endDate) {
                    dates.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 1);
                  }
                  break;
                case "month":
                  startDate.setMonth(endDate.getMonth() - 1);
                  currentDate = new Date(startDate);
                  currentDate.setDate(currentDate.getDate() - currentDate.getDay());
                  currentDate.setHours(0, 0, 0, 0);
                  while (currentDate <= endDate) {
                    dates.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 7);
                  }
                  break;
                case "6months":
                  startDate.setMonth(endDate.getMonth() - 6);
                  currentDate = new Date(startDate);
                  currentDate.setDate(1);
                  currentDate.setHours(0, 0, 0, 0);
                  while (currentDate <= endDate) {
                    dates.push(new Date(currentDate));
                    currentDate.setMonth(currentDate.getMonth() + 1);
                  }
                  break;
                case "year":
                  startDate.setFullYear(endDate.getFullYear() - 1);
                  currentDate = new Date(startDate);
                  currentDate.setDate(1);
                  currentDate.setHours(0, 0, 0, 0);
                  while (currentDate <= endDate) {
                    dates.push(new Date(currentDate));
                    currentDate.setMonth(currentDate.getMonth() + 2);
                  }
                  break;
                default:
                  if (weightLogs.length > 0) {
                    startDate = new Date(Math.min(...weightLogs.map(log => new Date(log.log_date))));
                    currentDate = new Date(startDate);
                    currentDate.setDate(1);
                    currentDate.setHours(0, 0, 0, 0);
                    while (currentDate <= endDate) {
                      dates.push(new Date(currentDate));
                      currentDate.setMonth(currentDate.getMonth() + 1);
                    }
                  }
              }

              return dates;
            })()}
            tickFormat={(t) => {
              const date = new Date(t);
              switch (viewRange) {
                case "week":
                  return date.toLocaleDateString("en-US", { weekday: 'short' });
                case "month":
                  return date.getDate().toString();
                case "6months":
                  return date.toLocaleDateString("en-US", { month: 'short' });
                case "year":
                  return date.getFullYear() !== new Date().getFullYear()
                    ? date.toLocaleDateString("en-US", { month: 'short', year: '2-digit' })
                    : date.toLocaleDateString("en-US", { month: 'short' });
                default:
                  return date.toLocaleDateString("en-US", { month: 'short', year: 'numeric' });
              }
            }}
            style={{ 
              tickLabels: { 
                fontSize: 10, 
                padding: 5,
                textAnchor: 'start'
              },
              grid: {
                stroke: "rgba(255, 255, 255, 0.2)",
                strokeWidth: 1
              }
            }}
            domain={(() => {
              const endDate = new Date(currentDateRange);
              let startDate = new Date(currentDateRange);

              switch (viewRange) {
                case "week":
                  startDate.setDate(endDate.getDate() - 7);
                  break;
                case "month":
                  startDate.setMonth(endDate.getMonth() - 1);
                  break;
                case "6months":
                  startDate.setMonth(endDate.getMonth() - 6);
                  break;
                case "year":
                  startDate.setFullYear(endDate.getFullYear() - 1);
                  break;
                default:
                  if (weightLogs.length > 0) {
                    startDate = new Date(Math.min(...weightLogs.map(log => new Date(log.log_date))));
                  } else {
                    startDate = new Date();
                    startDate.setMonth(endDate.getMonth() - 1);
                  }
              }

              // Align domain dates properly
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(23, 59, 59, 999);

              return [startDate, endDate];
            })()}
          />

          <VictoryAxis 
            dependentAxis 
            domain={calculateAxisDomain(filterLogs())}
            tickValues={getTickValues(filterLogs())}
            style={{
              grid: {
                stroke: "rgba(255, 255, 255, 0.2)",
                strokeWidth: 1
              },
              tickLabels: { fontSize: 10 }
            }}
          />
          
          <VictoryLine
            data={filterLogs()}
            interpolation="linear"
            style={{
              data: { stroke: dark_blue, strokeWidth: 2 },
            }}
          />
          
          <VictoryScatter
            data={filterLogs().filter((log) => log.y !== null)}
            size={decidePlotSize()}
            style={{
              data: { fill: blue },
            }}
          />
        </VictoryChart>
      </div>
      
      {/* Enter Weight */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-pop-up-title">{editId !== null ? "Edit Weight Entry" : "Add Weight Entry"}</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body">
              <div className="input-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Enter weight in kg"
                  step="0.1"
                  min="30"
                  max="300"
                  required
                />
              </div>
              
              <div className="input-group">
                <label>Date & Time</label>
                <input
                  type="datetime-local"
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-primary"
                onClick={addOrUpdateWeightLog}
              >
                {editId !== null ? "Update Entry" : "Add Entry"}
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      

      <button onClick={() => { setIsModalOpen(true); setEditId(null); }} className="add-entry-button" > Add Entry</button>

      {/* Weight Logs List */}
      <h2>Weight Log</h2>
      <ul>
          {[...weightLogs]
        .sort((a, b) => new Date(b.log_date) - new Date(a.log_date))
        .map((log, index) => (
          <li className="weight-log" key={log.id || index}>
            <div className="log-content">
              <span className="weight">Weight: {log.weight} kg</span>
              <span className="date">
                Date: {new Date(log.log_date).toLocaleDateString('en-GB')}
              </span>
              <div className="actions">
                <button className="edit-button" onClick={() => openEditModal(log)}>
                  Edit
                </button>
                <button className="delete-button" onClick={() => deleteWeightLog(log.id)}>
                  Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Scales;
